import UserAccount from "./UserAccount.mjs";
import { query, showElement, hideElement } from "../utils.mjs";
import { findPointsToNextToken, REWARD_TYPE, Rewards } from './Rewards.mjs';
import WindCreekClient from "./WindCreekClient.mjs";
import { tokenTiers, tokenStep, getCurTierFromPoints, getNextTierFromPoints } from "./Rewards.mjs";
import { progressBarTimeline, pageTimeline } from "./my-rewards-animations.mjs";
import { initCalendar } from "./my-rewards-calendar.mjs";

UserAccount.windCreekClient = new WindCreekClient();

let rewardsPage = query("[data-my-rewards-page]");
let availableRewardsList = rewardsPage.querySelector(
  "[data-available-rewards-list]"
);
let pastRewardsList = rewardsPage.querySelector("[data-past-rewards-list]");

//PROGRESS BAR CODE
//Builds dot for bars and past tiers.
function buildDot(hasCheck, tier, label = "") {
  const dot = document.createElement("div");
  dot.classList.add(
    "dot",
    "d-flex",
    "items-center",
    "justify-content-center",
    "rounded-circle",
    "d-inline-flex",
    "flex-shrink-0"
  );
  dot.dataset.tier = tier;
  if (label) {
    dot.setAttribute("data-bs-toggle", "tooltip");
    dot.setAttribute("data-bs-placement", "bottom");
    dot.setAttribute("data-bs-title", label);
    new bootstrap.Tooltip(dot);
  }
  if (hasCheck) {
    const check = document.createElement("span");
    check.classList.add("material-icons", "fs-5", "m-auto");
    check.textContent = "check";
    dot.appendChild(check);
  }

  return dot;
}

//Build the text block for the indicator.
function buildLabel(tier, points, status = "current") {
  //First create the wrapper
  const textWrap = document.createElement("div");
  textWrap.classList.add("text-wrap", "position-absolute");
  const tierText = tier === "Token" ? "3 Prime Tokens" : tier;
  //Then create the content
  const text =
    status === "current" ?
      `<div class="text-md-center">
          <span class='d-block label label-sm'>Year-to-Date</span>
          <span class='d-block label label-md'>Points</span>
          <hr class='my-1 border-tier d-md-none' />
        </div>`
      : `<span class='d-block label-sm text-tier--dark lh-1'>${tierText}</span>`;
  const currentClasses =
    status === "current" ? "flex-md-row align-items-md-center" : "";
  const num = parseInt(points).toLocaleString();
  textWrap.innerHTML = `
    <div class="d-flex flex-column ${currentClasses}">
      ${text}
      <span class='value text-tier--med h3 mb-0'>${num}</span>
    </div>`;
  return textWrap;
}

//Builds full indicator (dot + text).
function buildIndicator(tier, points, status = "current") {
  const indicator = document.createElement("div");
  indicator.classList.add(
    "indicator-wrap",
    "d-flex",
    "gap-2",
    "align-items-end",
    "flex-md-column"
  );
  indicator.dataset.tier = tier.label.toLowerCase();
  indicator.dataset.status = status;

  const dot = buildDot(status === "current", tier.label);
  indicator.appendChild(dot);
  const label = buildLabel(tier.label, points, status);
  indicator.appendChild(label);
  return indicator;
}

//Builds a progress bar for the current tier.
function buildCurrentBar(tier, percentComplete, curPoints) {
  const curBarWrap = document.querySelector("[data-current-bar]");
  const curBar = document.createElement("div");
  const position = percentComplete > 50 ? "end" : "start";
  curBar.classList.add(
    "progress",
    "progress-current",
    "bg-tier--light-strong",
    "overflow-visible",
    position
  );
  curBar.setAttribute("role", "progressbar");
  curBar.setAttribute("aria-label", `${tier.label} Tier Progress`);
  curBar.setAttribute("aria-valuenow", percentComplete);
  curBar.setAttribute("aria-valuemin", 0);
  curBar.setAttribute("aria-valuemax", 100);
  curBar.setAttribute("data-tier", tier.label.toLowerCase());
  curBar.innerHTML = `
    <div class="progress-bar bg-tier" style="--current-bar-val: ${percentComplete}%"></div>`;
  const indicator = buildIndicator(tier, curPoints, "current");
  curBar.appendChild(indicator);
  curBarWrap.appendChild(curBar);
}

//Builds a progress bar for the future tiers.
function buildFutureBar(tier) {
  const futureBarsWrap = document.querySelector("[data-future-bars]");
  const futureBar = document.createElement("div");
  futureBar.dataset.tier = tier.label.toLowerCase();
  futureBar.classList.add(
    "progress",
    "progress-future",
    "bg-tier--light-strong",
    "overflow-visible"
  );
  futureBar.setAttribute("role", "progressbar");
  futureBar.setAttribute("aria-label", `${tier.label} Tier Progress`);
  futureBar.setAttribute("aria-valuenow", 0);
  futureBar.setAttribute("aria-valuemin", 0);
  futureBar.setAttribute("aria-valuemax", 100);
  futureBar.innerHTML = `
    <div class="progress-bar bg-tier" style="--current-bar-val: 0"></div>`;
  const indicator = buildIndicator(tier, tier.min, "future");
  futureBar.appendChild(indicator);
  futureBarsWrap.appendChild(futureBar);
}

//Sets up the data needed for the progress bar then builds it.
function setupProgressBar(profileData) {
  const progressBar = document.querySelector("[data-progress-bar]");
  const dotWrap = progressBar.querySelector("[data-past-dots]");
  const tierPoints = WindCreekData.rewardTiers;
  const curPoints = profileData.basePointsEarned;
  // const curTier = profileData.clubState;
  // const nextTier = profileData.nextClubState;
  const curTierObject = getCurTierFromPoints(curPoints);
  const curTier = curTierObject ? curTierObject.label : "Unknown";
  const nextTierObject = getNextTierFromPoints(curPoints);
  // const nextTier = nextTierObject ? nextTierObject.label : "Unknown";
  //TODO Switch back to const if the API is updated
  let pointsNeeded = nextTierObject.min - curPoints;
  let percentComplete = Math.round((curPoints / (curPoints + pointsNeeded)) * 100);
  if (UserAccount.isOnHighestTier(profileData)) {
    //BUILD TOKEN PROGRESS BAR
    let base = tierPoints[tierPoints.length - 1].min; //Fantasy Min
    let diff = 0;
    let thisToken;
    let nextToken;
    let tokensEarned = 0;

    //Figure out where we are in available steps
    const range = tokenTiers.find((tier) => curPoints >= tier.min && curPoints <= tier.max);

    if (curPoints < tokenTiers[0].min) {
      //Are we still working toward our first token tier?
      thisToken = tierPoints[tierPoints.length - 1];
      nextToken = tokenTiers[0];
    } else if (range) {
      //Or are we earning tokens from the preset tiers?
      thisToken = range;
      const rangeIndex = tokenTiers.indexOf(range);
      if (rangeIndex === tokenTiers.length - 1) {
        nextToken = {
          label: range.label,
          min: range.max + 1,
          max: range.max + tokenStep,
        };
      } else {
        nextToken = tokenTiers[rangeIndex + 1];
      }
      base = range.min;
      tokensEarned = rangeIndex + 1;
    }
    //Then we've moved beyond the tiers and into token steps
    else {
      base = tokenTiers[tokenTiers.length - 1].min;
      diff = Math.floor((curPoints - base) / tokenStep);
      base = diff > 0 ? diff * tokenStep + base : base;
      thisToken = {
        label: "Token",
        min: base,
        max: base + tokenStep - 1,
      };
      nextToken = {
        label: "Token",
        min: base + tokenStep,
        max: base + tokenStep * 2 - 1,
      };
      tokensEarned = diff + tokenTiers.length;
    }
    // TODO: Decide if this should be zero based or last value based
    //Zero Based
    // percentComplete = Math.round(
    //   (curPoints / nextToken.min) * 100
    // );
    //Last Value Based
    percentComplete = Math.round(
      ((curPoints - base) / (nextToken.min - base)) * 100
    );

    //First build the past earned dots for tiers and tokens
    //Tier dots
    tierPoints.forEach((tier) => {
      const dot = buildDot(
        true,
        tier.label.toLowerCase(),
        `${tier.label} Status`
      );
      dotWrap.appendChild(dot);
    });
    //Token dots
    Array(tokensEarned)
      .fill()
      .forEach((_, i) => {
        let val;
        if (i <= tokenTiers.length - 1) {
          val = tokenTiers[i].min;
        } else {
          val =
            tokenTiers[tokenTiers.length - 1].min +
            ((i + 1) - tokenTiers.length) * tokenStep;
        }
        const dot = buildDot(
          true,
          `token`,
          `3 Prime Tokens Earned at ${val.toLocaleString()} Points`
        );
        dotWrap.appendChild(dot);
      });

    //Then build the current token bar
    buildCurrentBar(thisToken, percentComplete, curPoints);

    //Finally build the future token bars
    buildFutureBar(nextToken);
  } else {
    //BUILD TIER LEVEL PROGRESS BAR
    tierPoints.forEach((tier,) => {
      //First build the past tier dots
      if (tier.label !== "Fantasy" && curPoints > tier.max) {
        const dot = buildDot(
          true,
          tier.label.toLowerCase(),
          `${tier.label} Status`
        );
        dotWrap.appendChild(dot);
      }
      //Then build the current tier bar
      else if (curTier === tier.label) {
        buildCurrentBar(tier, percentComplete, curPoints);
      } else {
        // Finally build the future tier bars
        buildFutureBar(tier);
      }
    });
  }
  progressBarTimeline();
}
//END PROGRESS BAR CODE

//MY REWARDS CARDS CODE
//Formats the date
function buildDateString(reward, style = "") {
  let type = Rewards.getType(reward);
  if (type === REWARD_TYPE.HOTEL_STAY) {
    return "";
  } else {
    let dateString;
    let start = reward.startDate || reward.validFromDateTime;
    let end = reward.endDate || reward.validToDateTime;
    if (style === "short") {
      dateString = `<span class="label-3 text-inherit">${start} to ${end}</span>`;
    } else {
      dateString = `Valid from ${start} to ${end}`;
    }

    return dateString;
  }
}

//Build calendar for modal
const calendarModal = document.getElementById("rewards-calendar-modal");
if (calendarModal) {
  calendarModal.addEventListener("show.bs.modal", (event) => {
    const button = event.relatedTarget;
    const startDate = button.getAttribute("data-bs-startDate");
    const endDate = button.getAttribute("data-bs-endDate");
    const validDays = button.getAttribute("data-bs-validDays");
    const blackoutDates = button.getAttribute("data-bs-blackoutDates");
    const validTimeString = button.getAttribute("data-bs-timeString");
    initCalendar(startDate, endDate, validDays, blackoutDates, validTimeString);
  });
}

//Build the calendar trigger button
function buildCalendarButton(reward, status) {
  if (status === "past") {
    return "";
  } else {
    const startDate = reward.validFromDateTimeUTC;
    const endDate = reward.validToDateTimeUTC;
    const validDaysOfWeek = reward.validDaysOfWeek;
    const blackoutDays = reward.blackoutDates;
    const validTimeString =
      reward.dailyValidFromTime !== null && reward.dailyValidToTime !== null ?
        reward.dailyValidFromTime + " – " + reward.dailyValidToTime :
        "";
    return `
    <button class="btn-outline-tier btn btn-sm " type='button' data-bs-toggle="modal" data-bs-target="#rewards-calendar-modal" data-bs-startDate="${startDate}" data-bs-endDate="${endDate}" data-bs-validDays="${validDaysOfWeek}" data-bs-blackoutDates="${blackoutDays}" data-bs-timeString="${validTimeString}">
    <span class="material-icons fs-5">date_range</span>
    Calendar
  </button>`;
  }
}

function buildPastReason(reward, status, date, isV2) {
  let pastReason = "";
  if (status === "current") {
    return pastReason;
  }
  if (isV2) {
    //V2 Past Reason
    if (reward.isExpired) {
      let endDate = reward.validToDateTime.replace(/^0/, "").replace("/0", "/");
      pastReason = `Expired: ${endDate}`;
    } else if (
      reward.assignmentState === "redeemed" ||
      reward.assignmentState === "settled"
    ) {
      pastReason = "Redeemed";
    }
  } else {
    //V1 Past Reason
    if (date.redeemedDate) {
      let formattedRedeemedDate =
        date.redeemedDate.getMonth() +
        1 +
        "/" +
        date.redeemedDate.getDate() +
        "/" +
        date.redeemedDate.getFullYear();
      pastReason = "Redeemed on " + formattedRedeemedDate;
    } else {
      let endDate = reward.endDate
        .replace(/^0/, "")
        .replace("/0", "/")
        .split(" ")[0];
      pastReason = `Expired: ${endDate}`;
    }
  }

  if (reward.isPartnerReward) {
    if (reward.event.eventType === 'voucher_program_code_redeemed') {
      pastReason = 'Redeemed ' + reward.event.eventDate;
    } else if (reward.event.eventType === 'voucher_program_canceled') {
      pastReason = 'Canceled ' + reward.event.eventDate;
    } else if (reward.event.eventType === 'voucher_program_expired') {
      pastReason = 'Expired ' + reward.event.eventDate;
    }
  }

  return `<span class='d-block'>${pastReason}</span>`;
}

function buildRewardCard(reward, status, index) {
  const isV2 = UserAccount.isMiamiSite();
  const date = Rewards.getDate(reward);

  //Set Classes that change based on status
  const classes = {};
  if (status === "current") {
    classes.cardClass = "bg-tier--light-em text-tier--dark";
    classes.titleColor = "text-tier--med";
  } else {
    classes.cardClass = "bg-white text-dark-subtle";
    classes.titleColor = "text-dark-subtle";
  }

  //Set the variables that change based on version
  let title = '';
  let id = '';
  let dateStringShort = '';
  let prizeCode = '';
  let calendarButton = '';
  let desc = '';
  let cardEnd = '';
  let cardBottom = '';
  let availableAndRedeemedQuantity = '';

  if (isV2) {
    // id = status + "-" + index;

    /* TODO: Future title implementation to handle different prizeTypes
    if (reward.plsPrize.prizeType === "valuePrizeType") {
      let value = parseInt(reward.plsPrize.value.replace(/[^0-9.-]+/g, ""));
      value = value.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });
      title = `<span class="h3 ${classes.titleColor}">${value}</span> ${reward.plsPrize.name}`;
    } else {
      title = reward.plsPrize.name;
    }
    */

    title = reward.plsPrize.name;
    dateStringShort = buildDateString(reward, "short");
    calendarButton = buildCalendarButton(reward, status);
    desc = reward.plsPrize.description;

    if (status === 'current' && reward.plsPrize.allowsPartialRedemption) {
      cardEnd = `<div class="d-flex gap-3">
        <span class='d-block label-2 mb-0 lh-sm'>Used: ${reward.usedValue}</span>
        <span class='d-block label-2 mb-0 lh-sm'>Remaining: ${reward.remainingValue}</span>
      </div>`;
    }
  } else {
    // id = reward.prizeCode.replace(/[^a-zA-Z0-9 ]/g, "");
    title = reward.prizeName;
    dateStringShort = buildDateString(reward, "short");
    prizeCode = `<p class='mb-0'>Prize Code: ${reward.prizeCode}</p>`;
  }

  if (reward.isPartnerReward) {
    title = `
      <div class="d-flex gap-2 align-items-center">
        <div>
          <svg width="60" height="60" class="rounded-circle" version="1.1" viewBox="0 0 2104.5 2104.5" xmlns="http://www.w3.org/2000/svg"><rect width="2104.5" height="2104.5" style="fill:#000000"/><path d="m595.67 1212c59.13 0 104.85-45.73 104.85-113.4v-263.37h64v426.76h-63.39v-39.63c-28.65 29.87-68.27 46.94-112.78 46.94-91.45 0-161.56-66.45-161.56-167v-267h64v263.3c0 68.89 45.11 113.4 104.86 113.4" style="fill:#ffffff"/><path d="m812.09 835.23h61.57v155.46a153.73 153.73 0 0 1 110.34-46.34c91.45 0 163.38 72.55 163.38 162.78 0 89.62-71.93 162.16-163.38 162.16a155.13 155.13 0 0 1-111-46.33v39h-61zm167.65 379.81c58.53 0 106.69-48.17 106.69-107.91 0-60.36-48.16-107.91-106.69-107.91-59.14 0-107.3 47.55-107.3 107.91 0 59.74 47.55 107.91 107.3 107.91" style="fill:#ffffff"/><path d="m1331.5 944.96c89.61 0 155.46 68.89 155.46 161.56v20.12h-252.97c8.54 50.6 50.6 88.4 103 88.4 36 0 66.46-14.64 89.62-45.73l44.51 32.92c-31.1 41.46-77.43 66.45-134.13 66.45-93.27 0-165.21-69.5-165.21-162.16 0-87.79 68.89-161.56 159.73-161.56m-96.33 132.91h189.6c-10.36-47.56-48.77-79.26-94.49-79.26s-84.13 31.7-95.11 79.26" style="fill:#ffffff"/><path d="m1652.2 1005.9c-40.24 0-69.5 31.09-69.5 79.25v176.8h-61.58v-310.92h61v38.44c15.24-25 40.24-40.85 74.38-40.85h21.33v57.31z" style="fill:#ffffff"/></svg>
        </div>
        <div>
          ${reward.prize.name}
        </div>
      </div>
    `;
    prizeCode = `<p class='mb-0'>Voucher Code: ${reward.redeemCode}</p>`;
    desc = reward.prize.description;
    if (status === 'current') {
      if (reward.event.eventType === 'voucher_program_code_claimed') {
        cardEnd = `
            <span class='d-block label-2 mb-0 lh-sm'>Claimed ${reward.event.eventDate}</span>
          `;
      } else {
        if (reward.redeemLink) {
          cardEnd = `
            <a href="${reward.redeemLink}" target="_blank" class="btn-outline-tier btn btn-sm me-auto mt-1" type="button" title="Visit uber.com">
              Claim
              <span class="material-icons fs-5">launch</span>
            </a>
          `;
        }

      }
    }
  }

  const pastReason = buildPastReason(reward, status, date, isV2);

  if (reward.maxPrizesPerPeriod > 1) {
    availableAndRedeemedQuantity = `
      <div class="d-flex gap-3">
        <div>Quantity: ${reward.maxPrizesPerPeriod}</div>
        <div>Redeemed: ${reward.redeemed}</div>
      </div>
    `;
  }

  if (desc || pastReason || cardEnd || availableAndRedeemedQuantity) {
    cardBottom = `<div class='vstack gap-2 pt-3 fs-sm'>
      ${desc}
      ${pastReason}
      ${cardEnd}
      ${availableAndRedeemedQuantity}
    </div>`;
  }

  return `
    <div class="col-md-6 col-lg-4">
      <div class="reward-card p-3 rounded border border-4 border-white h-100 ${
        classes.cardClass
      }">
        <div class='d-flex gap-3 justify-content-between align-items-center'>
          <h4 class="mb-0 ${classes.titleColor}">${title}</h4>
        </div>
        <div class='d-flex gap-3 justify-content-between align-items-center mt-2'>
          ${dateStringShort}
          ${calendarButton}
        </div>
        <div class='mt-2'>
          ${prizeCode}
        </div>
        ${cardBottom}
      </div>
    </div>
  `;
}
//MY REWARDS CARDS CODE END

// Comparison function for the Array.prototype.sort() method. Designed to sort an array of rewards by start date.
function compareByStartDate(a, b) {
  let startDateA = Rewards.getDate(a);
  let startDateB = Rewards.getDate(b);
  if (startDateA < startDateB) {
    return -1; // a comes before b
  } else if (startDateA > startDateB) {
    return 1; // a comes after b
  } else {
    return 0; // a and b are equal
  }
}

// This function populates the rewards data on the page
function setupRewardsPage(profileData, rewards, accountData) {
  let patronAssets = rewards?.patronAssets;
  let timeZoneHelpText = rewards.rewardsWithTimeZone.rewardTimeZoneHelpText;
  let partnerRewards = rewards.rewardsWithTimeZone?.partnerRewards ?? [];
  rewards = rewards.rewardsWithTimeZone.patronRewards;

  let pointsNeeded;
  if (UserAccount.isOnHighestTier(profileData)) {
    pointsNeeded = profileData.basePointsEarned + findPointsToNextToken(profileData.basePointsEarned);
  } else {
    const nextTier = getNextTierFromPoints(profileData.basePointsEarned);
    pointsNeeded = nextTier.min;
  }
  let tierProgressPercent= Math.round(
    (profileData.basePointsEarned / (pointsNeeded)) * 100
  );
  rewardsPage.querySelector("[data-tier-progress-percent]").textContent =
    tierProgressPercent + "%";

  rewardsPage.querySelector("[data-base-points]").textContent =
    profileData.basePoints.toLocaleString();
  rewardsPage.querySelector("[data-free-play]").textContent =
    "$" + profileData.freePlay.toLocaleString();

  UserAccount.getPrimeTokens(accountData.preferredLocation).then(primeTokens => {
    rewardsPage.querySelector("[data-prime-tokens]").textContent =
      primeTokens.toLocaleString();
  }).catch((error) => {
    // TODO: error handling
  });

  showElement(`[data-benefits-${profileData.clubState.toLowerCase()}]`);

  /*
   If the user logged in with their email + password, they can access the online account info (WScore and tickets).
   Otherwise, we need to hide those fields and display a message asking them to signin/signup.
   */
  if (
    patronAssets &&
    patronAssets.hasOwnProperty("wscore") &&
    patronAssets.hasOwnProperty("tickets")
  ) {
    rewardsPage.querySelector("[data-wscore]").textContent =
      patronAssets.wscore.toLocaleString();
  } else {
    showElement("[data-online-account-only-message]");
    hideElement("[data-online-account-only]");
  }

  let currentDate = new Date();
  rewardsPage.querySelector("[data-current-year]").textContent =
    currentDate.getFullYear();

  let rewardsGroupedByStatus = Rewards.groupByStatus(rewards, partnerRewards);

  // Generate the list of current/available rewards
  let availableRewardsHTML = "";

  if (rewardsGroupedByStatus.available.length > 0) {
    rewardsGroupedByStatus.available.sort(compareByStartDate).reverse();
    availableRewardsHTML += `<div class="row gy-4 pt-2">`;

    rewardsGroupedByStatus.available.forEach((reward, index) => {
      availableRewardsHTML += buildRewardCard(reward, "current", index);
    });
    availableRewardsHTML += `</div>`;
    availableRewardsHTML += `<p class="pt-4 fs-sm">${timeZoneHelpText}</p>`;
    //Add the calendar to the modal
    const calendarModal = document.getElementById("rewards-calendar-modal");
    if (calendarModal) {
      calendarModal.addEventListener("show.bs.modal", (event) => {
        const button = event.relatedTarget;
        const startDate = button.getAttribute("data-bs-startDate");
        const endDate = button.getAttribute("data-bs-endDate");
        const validDays = button.getAttribute("data-bs-validDays");
        const blackoutDates = button.getAttribute("data-bs-blackoutDates");
        const validTimeString = button.getAttribute("data-bs-timeString");
        initCalendar(
          startDate,
          endDate,
          validDays,
          blackoutDates,
          validTimeString
        );
      });
    }
  } else {
    availableRewardsHTML =
      '<div class="label-2 text-primary">No rewards available</div>';
  }

  availableRewardsList.innerHTML = availableRewardsHTML;

  // Generate the list of past (expired/used) rewards
  let pastRewardsHTML = "";

  if (rewardsGroupedByStatus.past.length > 0) {
    rewardsGroupedByStatus.past.sort(compareByStartDate).reverse();
    pastRewardsHTML += `<div class="row gy-4 pt-2">`;

    rewardsGroupedByStatus.past.forEach((reward, index) => {
      pastRewardsHTML += buildRewardCard(reward, "past", index);
    });
    pastRewardsHTML += `</div>`;
  } else {
    pastRewardsHTML = '<div class="label-2 text-primary">No past rewards</div>';
  }

  pastRewardsList.innerHTML = pastRewardsHTML;
}

if (rewardsPage) {
  if (UserAccount.isLoggedIn()) {
    // The user has an existing session. Call the API to retrieve the profile and rewards data.

    /*
    Getting all the necessary data requires multiple API calls, so we need to wait for everything to finish
    (i.e. wait for all the promises to resolve)
    */
    Promise.all([UserAccount.getProfile(), UserAccount.getRewards(), UserAccount.getAccountData()])
      .then(([profileData, rewards, accountData]) => {
        setupRewardsPage(profileData, rewards, accountData);
        setupProgressBar(profileData);
        rewardsPage.classList.remove("loading-animation");
        pageTimeline();
      })
      .catch((error) => {
        showElement("[data-page-error-message]");
        hideElement("[data-hide-on-page-error]");
        rewardsPage.classList.remove("loading-animation");
        console.log("unable to load profile data or rewards data");
        console.error(error);
        newrelic.noticeError(error);
      });
  } else {
    // User is not logged in. Redirect to home page and prompt for authentication.
    UserAccount.forceAuthentication();
  }
}
